import { usePromiseToQuery } from 'src/core/api/hooks/usePromiseToQuery';

import { useOrganisationCompanyList } from '../../../hooks/data/useOrganisationCompanyList';

export type Member = {
  id: string;
  email: string;
  avatar: string | null;
  fullname: string;
  isPending: boolean;
  isAccountOwner: boolean;
};

type Response = {
  id: string;
  email: string;
  avatar: string | null;
  fullname: string;
  pending: boolean;
  data_by_company?: {
    [key: string]: {
      is_account_owner?: boolean;
    };
  };
};

const fetchCompanyMembers = async (companyId: string) => {
  const response = await fetch(
    `${appConfig.apiUrls.api}/${companyId}/users?includePending=true`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.json();
};

const fetchAllMembers = async (companyIds: string[]) => {
  const memberPromises = companyIds.map((companyId) =>
    fetchCompanyMembers(companyId),
  );
  const memberResponses = await Promise.all(memberPromises);

  const members = memberResponses.flatMap((response: Response[]) =>
    response.map((member) => ({
      id: member.id,
      email: member.email,
      avatar: member.avatar,
      fullname: member.fullname,
      isPending: member.pending,
      isAccountOwner:
        member.data_by_company?.[companyIds[0]]?.is_account_owner ?? false,
    })),
  );

  return members.filter(
    (member, index, self) =>
      index === self.findIndex((m) => m.id === member.id),
  );
};

export const useOrganisationMembers = () => {
  const organisationCompanies = useOrganisationCompanyList();
  const companyIds = organisationCompanies.map((company) => company.id);

  return usePromiseToQuery<Member[]>(() => fetchAllMembers(companyIds));
};
